import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'es-toolkit/compat'
import useMap from 'hooks/use-map'
import {
  applyArcGISHillshadeSource,
  applyArcGISImagerySource,
  applyArcGISLabelsStyle,
  createArcGISHillshadeLayer,
  createArcGISImageryLayer,
  createArcGISLabelsLayer,
} from 'common/ol/arcgis-layers'
import type { BaseMapLayer } from 'common/ol/arcgis-layers'
import { keepArcGISSessionApplied } from 'common/ol/arcgis-session'
import type { AppState } from 'state/index.js'

type ArcGISMapsProps = {
  /** Which map to attach to: 'map' (default), or 'map_a'/'map_b' on the split view. */
  mapRef?: string
  /** Overlay roads, boundaries and place names. See common/ol/arcgis-layers. */
  labels?: boolean
}

/**
 * Esri World Imagery with the roads, boundaries and place labels that complete
 * the hybrid. Unlike the Google base map this replaces, it is made of ordinary
 * OpenLayers layers — no second mapping library re-renders the app's own vector
 * layers as its overlays, so nothing else on the map has to opt out.
 *
 * Both halves are served through one basemap session, renewed before it expires.
 * The label style follows the signed-in user's locale and disputed boundaries
 * follow the tenant's country, together replacing the region biasing the Google
 * base map did through its script URL.
 */
export default function ArcGISMaps({
  mapRef = 'map',
  labels = true,
}: ArcGISMapsProps) {
  const map = useMap(mapRef)
  const { i18n } = useTranslation()

  const locale = i18n.resolvedLanguage ?? i18n.language
  const countryId = useSelector(
    (state: AppState) => state.account.singleton?.country?.id,
  )
  /*
   * The tenant arrives after the first render, and the style URL depends on it.
   * Waiting for it means the style document is fetched once with the right
   * worldview rather than twice — once with none and once with it. The account
   * reducer starts this as {}, so emptiness is what distinguishes "not loaded
   * yet" from "loaded, no country set".
   */
  const tenantLoaded = useSelector(
    (state: AppState) => !isEmpty(state.account.singleton),
  )

  // Neither layer depends on anything that varies per render, so both are built
  // once and reconfigured in place — rebuilding them would re-request every tile.
  const imagery = useMemo(() => createArcGISImageryLayer(), [])
  // Built here so it shares the session, but invisible: nothing fetches its
  // tiles until the 3D map's Elevation surface switches it on.
  const hillshade = useMemo(() => createArcGISHillshadeLayer(), [])
  const labelsLayer = useMemo(
    () => (labels ? createArcGISLabelsLayer() : null),
    [labels],
  )

  const layers: BaseMapLayer[] = useMemo(
    () =>
      labelsLayer ? [imagery, hillshade, labelsLayer] : [imagery, hillshade],
    [imagery, hillshade, labelsLayer],
  )

  /*
   * Both halves are pointed at the same session, and re-pointed when it renews
   * or after a failed start is retried. There is deliberately no API-key
   * fallback: a session that cannot be started leaves the map without a base map
   * rather than silently reverting to per-request billing.
   */
  useEffect(() => {
    if (!tenantLoaded) return

    return keepArcGISSessionApplied(async (token) => {
      applyArcGISImagerySource(imagery, token)
      applyArcGISHillshadeSource(hillshade, token)
      if (labelsLayer) {
        await applyArcGISLabelsStyle(labelsLayer, token, { locale, countryId })
      }
    })
  }, [imagery, hillshade, labelsLayer, tenantLoaded, locale, countryId])

  useEffect(() => {
    if (!map) return

    layers.forEach((layer) => map.addLayer(layer))

    return () => {
      layers.forEach((layer) => map.removeLayer(layer))
    }
  }, [map, layers])

  return null
}
